import type { DiagnosticReport, DiagnosticsRuntimeState } from '@patsnap/synapse_common_utils'
import { createDiagnosticsRuntime } from '@patsnap/synapse_common_utils'
import Vue from 'vue'
import { VUE_APP_BUILD_VERSION, VUE_APP_ENV } from '../../config'
import { getLocaleLang } from '../../utils/business/locale'
import { configureAxiosDiagnosticSink, setAxiosDiagnosticsEnabled } from './adapters/axios'
import { configureBrowserDiagnosticSink, installBrowserDiagnosticAdapter, uninstallBrowserDiagnosticAdapter } from './adapters/browser'
import { configureIframeDiagnosticSink, installIframeDiagnosticAdapter, setIframeDiagnosticsEnabled } from './adapters/iframe'
import { configureDiagnosticRouteSink } from './adapters/router'
import {
  DIAGNOSTIC_CATEGORY_LIMITS,
  DIAGNOSTIC_NAMESPACE,
  DIAGNOSTIC_RECORDING_STORAGE_KEY,
  DIAGNOSTIC_SCHEMA_VERSION,
  DIAGNOSTIC_SOURCE_APP,
  DIAGNOSTIC_STORAGE_DEBOUNCE_MS,
  DIAGNOSTIC_STORAGE_KEY,
  DIAGNOSTIC_STORAGE_MAX_BYTES,
  DIAGNOSTIC_WINDOW_MS,
} from './config'

type DiagnosticRecordingEndReason = 'manual' | 'timeout'

const diagnosticsRuntime = createDiagnosticsRuntime({
  namespace: DIAGNOSTIC_NAMESPACE,
  sourceApp: DIAGNOSTIC_SOURCE_APP,
  schemaVersion: DIAGNOSTIC_SCHEMA_VERSION,
  durationMs: DIAGNOSTIC_WINDOW_MS,
  maxStorageBytes: DIAGNOSTIC_STORAGE_MAX_BYTES,
  storageDebounceMs: DIAGNOSTIC_STORAGE_DEBOUNCE_MS,
  categoryLimits: DIAGNOSTIC_CATEGORY_LIMITS,
  eventsStorageKey: DIAGNOSTIC_STORAGE_KEY,
  stateStorageKey: DIAGNOSTIC_RECORDING_STORAGE_KEY,
  environmentProvider: () => ({
    buildVersion: VUE_APP_BUILD_VERSION || '',
    locale: getLocaleLang(),
    env: VUE_APP_ENV || '',
  }),
})

let initialized = false
let pendingDiscardReason: DiagnosticRecordingEndReason | undefined
const initialRuntimeState = diagnosticsRuntime.getState()

export const diagnosticRecordingState = Vue.observable<{ status: 'idle' | 'recording' | 'finished' }>({
  status: initialRuntimeState.status,
})
export const diagnosticRecordingEndState = Vue.observable<{ reason: DiagnosticRecordingEndReason | undefined }>({ reason: undefined })

export function initDiagnostics(): void {
  if (initialized || typeof window === 'undefined' || typeof document === 'undefined') return
  initialized = true

  try {
    configureBrowserDiagnosticSink(diagnosticsRuntime.record)
    configureAxiosDiagnosticSink(diagnosticsRuntime.record)
    configureIframeDiagnosticSink(diagnosticsRuntime.record)
    configureDiagnosticRouteSink(diagnosticsRuntime.record)
    installIframeDiagnosticAdapter()
    diagnosticsRuntime.subscribe(syncDiagnosticsRuntimeState)
    syncDiagnosticsRuntimeState(diagnosticsRuntime.getState())
  } catch {
    // Diagnostics must not prevent application startup.
  }
}

function syncDiagnosticsRuntimeState(state: DiagnosticsRuntimeState): void {
  diagnosticRecordingState.status = state.status
  if (state.status === 'recording') {
    diagnosticRecordingEndState.reason = undefined
    installBrowserDiagnosticAdapter()
    setAxiosDiagnosticsEnabled(true)
    setIframeDiagnosticsEnabled(true)
    return
  }

  setIframeDiagnosticsEnabled(false)
  setAxiosDiagnosticsEnabled(false)
  uninstallBrowserDiagnosticAdapter()

  if (state.status === 'finished') {
    diagnosticRecordingEndState.reason = 'timeout'
  } else if (pendingDiscardReason) {
    diagnosticRecordingEndState.reason = pendingDiscardReason
  }
}

export function startDiagnosticsRecording(): void {
  initDiagnostics()
  diagnosticRecordingEndState.reason = undefined
  diagnosticsRuntime.start()
}

export function finishDiagnosticsRecording(): void {
  diagnosticRecordingEndState.reason = 'timeout'
  diagnosticsRuntime.finish()
}

export function discardDiagnosticsRecording(reason: DiagnosticRecordingEndReason = 'manual'): void {
  pendingDiscardReason = reason
  diagnosticRecordingEndState.reason = reason
  diagnosticsRuntime.discard()
  syncDiagnosticsRuntimeState(diagnosticsRuntime.getState())
  pendingDiscardReason = undefined
}

export function isDiagnosticsRecording(): boolean {
  return diagnosticsRuntime.getState().status === 'recording'
}

export function getDiagnosticRecordingStartedAt(): number | undefined {
  const state = diagnosticsRuntime.getState()
  return state.status === 'recording' ? state.startedAt : undefined
}

export function createCurrentDiagnosticReport(): Promise<DiagnosticReport> {
  return diagnosticsRuntime.createReport()
}

export { recordDiagnosticRoute } from './adapters/router'
export { downloadDiagnosticReport } from './exporter'
